import { Checkbox, Input, Label, RadioGroup, Select, Text, toast } from "@medusajs/ui"
import { useEffect, useMemo, useState } from "react"
import { Link } from "react-router-dom"
import { Field } from "@/components/form"
import { AddressFields } from "@/components/address-fields"
import { ErrorState, SaveBar, Section } from "@/components/ui"
import { useCan } from "@/lib/permissions"
import { errorMessage } from "@/lib/sdk"
import { useGeneralSettings, useSaveGeneralSettings, type GeneralSettings } from "@/lib/settings"

/** Every IANA time zone, labelled "(GMT+05:30) Asia/Kolkata" and sorted by offset. */
const ZONES: [string, string][] = (() => {
  const ids: string[] = (Intl as any).supportedValuesOf?.("timeZone") ?? ["America/New_York", "UTC", "Europe/London", "Asia/Kolkata"]
  const offset = (tz: string) => {
    try {
      const part = new Intl.DateTimeFormat("en-US", { timeZone: tz, timeZoneName: "shortOffset" }).formatToParts(new Date()).find((p) => p.type === "timeZoneName")?.value ?? "GMT"
      const m = part.match(/GMT([+-])(\d{1,2})(?::(\d{2}))?/)
      return m ? (m[1] === "-" ? -1 : 1) * (Number(m[2]) * 60 + Number(m[3] ?? 0)) : 0
    } catch {
      return 0
    }
  }
  const fmt = (min: number) => `(GMT${min < 0 ? "-" : "+"}${String(Math.floor(Math.abs(min) / 60)).padStart(2, "0")}:${String(Math.abs(min) % 60).padStart(2, "0")})`
  const list = [...new Set(["UTC", ...ids])].map((id) => ({ id, o: offset(id) }))
  return list.sort((a, b) => a.o - b.o || a.id.localeCompare(b.id)).map(({ id, o }) => [id, `${fmt(o)} ${id.replace(/_/g, " ")}`])
})()

export function SettingsTitle({ title, subtitle }: { title: string; subtitle?: string }) {
  return (
    <div className="mb-6">
      <h1 className="k-h1">{title}</h1>
      {subtitle && <Text size="small" className="mt-1 text-ui-fg-subtle">{subtitle}</Text>}
    </div>
  )
}

export function SettingsGroup({ title, description, children }: { title: string; description?: string; children: React.ReactNode }) {
  return (
    <div className="mb-6">
      <h2 className="mb-1 text-[14px] font-semibold">{title}</h2>
      {description && <Text size="small" className="mb-2 text-ui-fg-subtle">{description}</Text>}
      <div className="mt-2">{children}</div>
    </div>
  )
}

/** Settings → General, laid out like Shopify's. */
export function GeneralSettingsPage() {
  const { data, error } = useGeneralSettings()
  if (error) return <ErrorState error={error} />
  if (!data) return null
  return <GeneralForm initial={data.settings} />
}

function GeneralForm({ initial }: { initial: GeneralSettings }) {
  const can = useCan()
  const canWrite = can("settings:write")
  const canLogs = can("logs:read")
  const [s, setS] = useState(initial)
  const save = useSaveGeneralSettings()
  const base = useMemo(() => initial, [initial])
  useEffect(() => setS(base), [base])
  const dirty = JSON.stringify(s) !== JSON.stringify(base)
  const set = <K extends keyof GeneralSettings>(k: K, v: GeneralSettings[K]) => setS((x) => ({ ...x, [k]: v }))

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!s.store_name.trim()) return toast.error("Store name can't be blank.")
    save.mutate(s, { onSuccess: () => toast.success("Settings saved"), onError: (err) => toast.error(errorMessage(err)) })
  }

  return (
    <form onSubmit={onSubmit} className="pb-24">
      <SettingsTitle title="General" />
      <fieldset disabled={!canWrite}>
        <SettingsGroup title="Store details">
          <Section>
            <div className="grid gap-4 px-5 py-4 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <Field label="Store name" hint="Shown on your storefront, emails and in this admin.">
                  <Input value={s.store_name} onChange={(e) => set("store_name", e.target.value)} />
                </Field>
              </div>
              <Field label="Store email" hint="Customers see this when you email them.">
                <Input type="email" value={s.store_email} onChange={(e) => set("store_email", e.target.value)} />
              </Field>
              <Field label="Store phone">
                <Input value={s.store_phone} onChange={(e) => set("store_phone", e.target.value)} />
              </Field>
            </div>
          </Section>
        </SettingsGroup>

        <SettingsGroup title="Billing address" description="Your business address, shown on invoices and used to calculate taxes.">
          <Section>
            <div className="grid gap-4 px-5 py-4 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <Field label="Legal business name">
                  <Input value={s.legal_name} onChange={(e) => set("legal_name", e.target.value)} />
                </Field>
              </div>
              <div className="sm:col-span-2">
                <AddressFields
                  value={{ ...s.address, country_code: (s.address.country_code || "US").toLowerCase() }}
                  onChange={(a) => setS((x) => ({ ...x, address: { address_1: a.address_1 ?? "", address_2: a.address_2 ?? "", city: a.city ?? "", province: a.province ?? "", postal_code: a.postal_code ?? "", country_code: (a.country_code ?? "us").toUpperCase() } }))}
                  name={false}
                  company={false}
                  phone={false}
                />
              </div>
            </div>
          </Section>
        </SettingsGroup>

        <SettingsGroup title="Store defaults">
          <Section>
            <div className="flex items-center justify-between px-5 py-4">
              <div>
                <Text size="small" weight="plus">Currency display</Text>
                <Text size="xsmall" className="text-ui-fg-subtle">Prices are shown and charged in this currency in every market. <Link to="/settings/markets" className="text-ui-fg-interactive">Markets</Link></Text>
              </div>
              <span className="rounded-lg bg-ui-bg-component px-2 py-1 text-[12px] font-medium">US Dollar (USD $)</span>
            </div>
            <div className="grid gap-4 px-5 py-4 sm:grid-cols-2">
              <Field label="Unit system">
                <Select value={s.unit_system} onValueChange={(v) => setS((x) => ({ ...x, unit_system: v as any, weight_unit: v === "metric" ? "kg" : "lb" }))}>
                  <Select.Trigger><Select.Value /></Select.Trigger>
                  <Select.Content>
                    <Select.Item value="imperial">Imperial system</Select.Item>
                    <Select.Item value="metric">Metric system</Select.Item>
                  </Select.Content>
                </Select>
              </Field>
              <Field label="Default weight unit">
                <Select value={s.weight_unit} onValueChange={(v) => set("weight_unit", v as any)}>
                  <Select.Trigger><Select.Value /></Select.Trigger>
                  <Select.Content>
                    {(s.unit_system === "metric" ? [["kg", "Kilogram (kg)"], ["g", "Gram (g)"]] : [["lb", "Pound (lb)"], ["oz", "Ounce (oz)"]]).map(([v, l]) => (
                      <Select.Item key={v} value={v}>{l}</Select.Item>
                    ))}
                  </Select.Content>
                </Select>
              </Field>
              <div className="sm:col-span-2">
                <Field label="Time zone" hint="Sets the time for when orders and analytics are recorded.">
                  <Select value={s.timezone} onValueChange={(v) => set("timezone", v)}>
                    <Select.Trigger><Select.Value /></Select.Trigger>
                    <Select.Content>{ZONES.map(([v, l]) => <Select.Item key={v} value={v}>{l}</Select.Item>)}</Select.Content>
                  </Select>
                </Field>
              </div>
            </div>
          </Section>
        </SettingsGroup>

        <SettingsGroup title="Order ID format" description="Shown on the order page, customer pages, and customer order notifications to identify the order.">
          <Section>
            <div className="grid gap-4 px-5 py-4 sm:grid-cols-2">
              <Field label="Prefix">
                <Input value={s.order_prefix} maxLength={10} onChange={(e) => set("order_prefix", e.target.value)} />
              </Field>
              <Field label="Suffix">
                <Input value={s.order_suffix} maxLength={10} onChange={(e) => set("order_suffix", e.target.value)} />
              </Field>
              <Text size="xsmall" className="text-ui-fg-subtle sm:col-span-2">
                Your order ID will appear as {[1001, 1002, 1003].map((n) => `${s.order_prefix}${n}${s.order_suffix}`).join(", ")}, …
              </Text>
            </div>
          </Section>
        </SettingsGroup>

        <SettingsGroup title="Order processing">
          <Section>
            <div className="flex flex-col gap-y-3 px-5 py-4">
              <Label size="small" weight="plus">After an order has been paid</Label>
              <RadioGroup value={s.auto_fulfill} onValueChange={(v) => set("auto_fulfill", v as any)} className="flex flex-col gap-y-2">
                {[
                  ["all", "Automatically fulfill the order's line items"],
                  ["gift_cards", "Automatically fulfill only the gift cards of the order"],
                  ["none", "Don't fulfill any of the order's line items automatically"],
                ].map(([v, l]) => (
                  <label key={v} className="flex items-center gap-x-2">
                    <RadioGroup.Item value={v} />
                    <Text size="small">{l}</Text>
                  </label>
                ))}
              </RadioGroup>
            </div>
            <div className="flex flex-col gap-y-2 px-5 py-4">
              <Label size="small" weight="plus">After an order has been fulfilled and paid, or when all items have been refunded</Label>
              <label className="flex items-start gap-x-2">
                <Checkbox className="mt-0.5" checked={s.auto_archive} onCheckedChange={(c) => set("auto_archive", c === true)} />
                <div>
                  <Text size="small">Automatically archive the order</Text>
                  <Text size="xsmall" className="text-ui-fg-subtle">The order will be removed from your list of open orders.</Text>
                </div>
              </label>
            </div>
          </Section>
        </SettingsGroup>

        {canLogs && (
        <SettingsGroup title="Resources">
          <Section>
            <Link to="/settings/activity" className="flex items-center justify-between px-5 py-3 hover:bg-ui-bg-base-hover">
              <Text size="small" weight="plus">Store activity log</Text>
              <Text size="xsmall" className="text-ui-fg-subtle">Every change staff made</Text>
            </Link>
          </Section>
        </SettingsGroup>
        )}
      </fieldset>
      {canWrite && <SaveBar visible={dirty} message="Unsaved changes" saving={save.isPending} onDiscard={() => setS(base)} />}
    </form>
  )
}
